/**
 * Affiliate commission tiers by new paid referrals in the current calendar month.
 *
 * Source of truth (marketing):
 * `marketing/presentations/content/presentation_affiliate_000002_pitch_deck.json`
 * (frame "How do affiliates make money?")
 *
 * Frontend-only for now — keep in sync if the presentation tiers change.
 * The rate applies to payments from the affiliate's entire active referral base,
 * not just the new referrals that unlocked the tier.
 */

export const AFFILIATE_COMMISSION_TIERS = [
  { minNewReferrals: 0, maxNewReferrals: 4, percent: 20, label: "0–4 new referrals" },
  { minNewReferrals: 5, maxNewReferrals: 9, percent: 25, label: "5–9 new referrals" },
  { minNewReferrals: 10, maxNewReferrals: 19, percent: 30, label: "10–19 new referrals" },
  { minNewReferrals: 20, maxNewReferrals: null, percent: 40, label: "20+ new referrals" },
];

/**
 * @param {number} newReferralsThisMonth
 * @returns {{ minNewReferrals: number, maxNewReferrals: number|null, percent: number, label: string }}
 */
export const getCommissionTierForNewReferrals = (newReferralsThisMonth) => {
  const n = Math.max(0, Number(newReferralsThisMonth) || 0);
  for (const tier of AFFILIATE_COMMISSION_TIERS) {
    const withinMax = tier.maxNewReferrals == null || n <= tier.maxNewReferrals;
    if (n >= tier.minNewReferrals && withinMax) return tier;
  }
  return AFFILIATE_COMMISSION_TIERS[0];
};

/**
 * @param {number} newReferralsThisMonth
 * @returns {{ newLabel: string, commissionLabel: string }}
 */
export const getCommissionTierProgressParts = (newReferralsThisMonth) => {
  const n = Math.max(0, Number(newReferralsThisMonth) || 0);
  const tier = getCommissionTierForNewReferrals(n);
  return {
    newLabel: n === 1 ? "1 new" : `${n} new`,
    commissionLabel: `${tier.percent}% commission on all paying affiliate users`,
  };
};

/**
 * @param {number} newReferralsThisMonth
 * @returns {string} e.g. "3 new = 25% commission on all paying affiliate users"
 */
export const formatCommissionTierProgress = (newReferralsThisMonth) => {
  const { newLabel, commissionLabel } = getCommissionTierProgressParts(newReferralsThisMonth);
  return `${newLabel} = ${commissionLabel}`;
};
